import { CustomValidationRenderer } from '@rightship-tech/common-ui/client-validation-system/custom-validation-renderer';
import { autoinject, computedFrom } from "aurelia-framework";
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { RightShipInspectionDetailsModel } from '../../../models/rightship-inspections.models';
import { RightshipInspectionService } from '../../../services/rightship-inspection.service';
import { NotificationClientService, NotificationType } from '@rightship-tech/common-ui/services/basic/notification-client.service';
import { Router } from 'aurelia-router';
import { RsInspectionRouteConstants } from '../../../constants/route.constants';
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import { RsInspectionGoogleAnalyticsActionName } from '../../../constants/google-analytics.constants';
import { TitleConst } from '../../../constants/title.constaints';
import { RightShipInspectionStatusEnum } from '@rightship-tech/rs-inspections-ui/models/rightship-inspection.models';
import { CacheClientService, CacheLocation } from '@rightship-tech/common-ui/services/basic/client-caching.service';
import { LookupClientService } from '@rightship-tech/common-ui/services/lookups/lookup-client.service';
import { LookupValueQuery } from '@rightship-tech/common-ui/constants/lookup.constants';
import { LookupValue, LookupValueRequest } from '@rightship-tech/common-ui/models/lookup.models';

@autoinject
export class WithDrawInspection {
    public readonly title: string = TitleConst.WithdrawInspection;
    public reason: string;
    public inspectionModel: RightShipInspectionDetailsModel;
    public messageCenterUrl: string;
    public cancellationReasons: LookupValue[] = [];
    public cancellationReasonId: number;

    @computedFrom("cancellationReasonId", "cancellationReasons")
    get isOtherReason(): boolean {        
        return this.cancellationReasonName.toLowerCase() == "other";
    }

    @computedFrom("cancellationReasonId", "cancellationReasons")
    get cancellationReasonName(): string {
        const cancellationReason = this.cancellationReasons.find(x => x.id == this.cancellationReasonId);
        return cancellationReason ? cancellationReason.name : '';
    }

    private _validator: ValidationController;
    private _encryptedData: string;
    private _rsInspectionId: number;    
    
    constructor(validationControllerFactory: ValidationControllerFactory, private rightshipInspectionService: RightshipInspectionService,
        private googleAnalyticsService: GoogleAnalyticsService, private cacheClientService: CacheClientService, private lookupService: LookupClientService,
        private router: Router, private notificationClientService: NotificationClientService) {
        this._validator = validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());
    }

    async activate(params: { encryptedData: string }) {
        this._encryptedData = decodeURIComponent(params.encryptedData);
        this.inspectionModel = await this.rightshipInspectionService.getRightShipInspectionDetails(this._encryptedData);
        this._rsInspectionId = this.inspectionModel.rightShipInspectionId;
        this.messageCenterUrl = `/inspection/${params.encryptedData}`;
        
        if(this.inspectionModel) {
            if (!this.inspectionModel.assignmentStatusActive
                || this.inspectionModel.statusId == RightShipInspectionStatusEnum.Acceptable
                || this.inspectionModel.statusId == RightShipInspectionStatusEnum.Acceptable
                || this.inspectionModel.statusId == RightShipInspectionStatusEnum.Unacceptable
            ) {
                this.router.navigateToRoute(RsInspectionRouteConstants.CloseRequest, { 
                    isDeclined: false,
                    encryptedData: encodeURIComponent(this._encryptedData)
                });
                return;
            }
        }
    }

    async attached() {
        this.getInvoiceDetails();
        this.setUpValidation();

        //Get lookup values for inspection cancellation reasons with availableToInspector = true
        const lookups = await this.lookupService.getLookup(new LookupValueRequest(LookupValueQuery.InspectionCancellationReasons, "null,0,3"));        
        this.cancellationReasons = lookups.activeValues.sort((a, b) => a.name.localeCompare(b.name));
    }

    getInvoiceDetails() {
    }

    setUpValidation() {
        this.getRules();
    }

    getRules(){
        ValidationRules
            .ensure<WithDrawInspection, number>(x => x.cancellationReasonId)
            .required()
            .withMessage("Please select a reason for withdrawing inspection")

            .ensure<string>(x => x.reason)
            .required()
            .withMessage("Please enter specific reason for withdrawing inspection")
            .when(x => x.isOtherReason)

            .on(this);
    }

    selectCancellationReason(cancellationReason: LookupValue) {
        this.cancellationReasonId = cancellationReason.id;

        if (!this.isOtherReason) {
            this.reason = '';
            if (this._validator.errors) this._validator.reset();
        }
    }
    
    async save() {
        const validator = await this._validator.validate();
        const email = await this.cacheClientService.get('user-email', CacheLocation.LocalStorage, false);
        if (validator.valid) {
            try {
                const response = await this.rightshipInspectionService.withdrawInspection(this._encryptedData, {
                    withdrawalOption: this.cancellationReasonName,
                    withdrawalReason: this.isOtherReason ? this.reason ?? '' : this.cancellationReasonName
                });

                //Send GA4 event
                this.googleAnalyticsService.sendCustomGA4EventMultiAnonymously(RsInspectionGoogleAnalyticsActionName.WithdrawOffer, {}, this.inspectionModel.emailIdentifier);

                this.router.navigateToRoute(RsInspectionRouteConstants.SubmittedWithdrawal, { 
                    vesselName: response.vesselName,
                    vesselImo: response.vesselImo, 
                    portName: response.port,
                    country: response.country,
                    requestedInspectionDate: response.requestedInspectionDate,
                    rsInspectionId: this._rsInspectionId,
                    encryptedData: encodeURIComponent(this._encryptedData)
                });

            } catch (error) {
                this.notificationClientService.show(`User ${email} cannot perform this action`, "", NotificationType.Error);
            }
            
        }
    }
}